import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '../api/client';

// Pipeline Insights PR 1 — hooks for the resolver + object views.
// Backend: /api/admin/pipeline-insights/{resolve,signal/{id},deal/{kind}/{key}}.

export interface SearchListHit {
  kind: string;
  id: string | number;
  label?: string;
  sublabel?: string;
}

export type ResolveResult =
  | { kind: 'signal'; signal_id: number }
  | { kind: 'deal'; deal_kind: 'candidate' | 'affinity_opp'; deal_key: string }
  | { kind: 'list'; results: SearchListHit[] };

export interface StepperStage {
  name: string;
  status: 'passed' | 'skipped' | 'failed' | 'changed' | 'refuted' | 'pending';
  detail: string | null;
}

export interface InsightsView {
  kind: 'signal' | 'deal';
  object_id: string;
  summary: string;
  stepper: StepperStage[];
}

export interface SignalViewResponse {
  view: InsightsView;
  // `timeline` reuses the existing ForwardTraceResponse shape; the FE
  // currently renders it via the Trace tab cards.
  timeline: unknown;
}

export interface DealViewResponse {
  view: InsightsView;
  timeline: unknown;
}

export interface AggregatorSuggestionDetail {
  id: number;
  deal_kind: string;
  deal_key: string;
  created_at: string | null;
  last_event_log_id: number;
  fields: Record<string, unknown>;
  synthesized_description: string | null;
  health_flags: unknown[];
  noise_event_ids: unknown[];
  proposed_changes_since_last_log: unknown[];
  tentative_bindings_to_review: unknown[];
  dismissal_aware_suppression: unknown[];
  ai_note_title: string | null;
  ai_note_body_html: string | null;
}

export interface AggregatorRunDetail {
  run: {
    id: number;
    run_id: number;
    deal_kind: string;
    deal_key: string;
    started_at: string | null;
    finished_at: string | null;
    model: string;
    status: string;
    error: string | null;
    events_processed: number;
    events_processed_detail: Record<string, unknown>;
    input_tokens: number;
    output_tokens: number;
    cost_usd: number;
    output_suggestion_id: number | null;
    prompt_version_id: number | null;
    prompt_version_tag: string | null;
    prompt_invocation_id: number | null;
  };
  suggestion: AggregatorSuggestionDetail | null;
}

// Queue-only Phase B (owner ruling 2026-08-20) — the rerun is enqueued
// (`phase-b-deal-rerun`), not run inline, so the response is just the queue
// receipt. No `restored_cursor` / run id / suggestion id / token counts /
// cost / error — those described an inline run that no longer happens here.
export interface CandidateAggregatorRerunResponse {
  status: string;
  job_id: number;
  deal_kind: 'candidate';
  deal_key: string;
  mode: 'full_replay';
  lookback: string;
}

export function useResolveQuery(q: string, enabled: boolean) {
  const trimmed = q.trim();
  return useQuery<ResolveResult>({
    queryKey: ['pipeline-insights', 'resolve', trimmed],
    queryFn: () =>
      apiFetch<ResolveResult>(
        `/api/admin/pipeline-insights/resolve?q=${encodeURIComponent(trimmed)}`,
      ),
    enabled: enabled && trimmed.length > 0,
    staleTime: 30_000,
  });
}

export function useSignalView(signalId: number | null) {
  return useQuery<SignalViewResponse>({
    queryKey: ['pipeline-insights', 'signal', signalId],
    queryFn: () =>
      apiFetch<SignalViewResponse>(
        `/api/admin/pipeline-insights/signal/${signalId}`,
      ),
    enabled: signalId !== null,
    staleTime: 30_000,
  });
}

export function useDealView(
  dealKind: 'candidate' | 'affinity_opp' | null,
  dealKey: string | null,
) {
  return useQuery<DealViewResponse>({
    queryKey: ['pipeline-insights', 'deal', dealKind, dealKey],
    queryFn: () =>
      apiFetch<DealViewResponse>(
        `/api/admin/pipeline-insights/deal/${dealKind}/${encodeURIComponent(dealKey || '')}`,
      ),
    enabled: dealKind !== null && !!dealKey,
    staleTime: 30_000,
  });
}

export function useAggregatorRunDetail(runId: number | null) {
  return useQuery<AggregatorRunDetail>({
    queryKey: ['pipeline-insights', 'aggregator-run', runId],
    queryFn: () =>
      apiFetch<AggregatorRunDetail>(
        `/api/admin/pipeline-insights/aggregator-runs/${runId}`,
      ),
    enabled: runId !== null,
    staleTime: 30_000,
  });
}

export function useCandidateAggregatorRerun() {
  const qc = useQueryClient();
  return useMutation<
    CandidateAggregatorRerunResponse,
    Error,
    { dealKey: string; reason?: string }
  >({
    mutationFn: ({ dealKey, reason }) =>
      apiFetch<CandidateAggregatorRerunResponse>(
        `/api/admin/pipeline-insights/deal/candidate/${encodeURIComponent(dealKey)}/aggregate-rerun`,
        {
          method: 'POST',
          body: JSON.stringify({ reason: reason?.trim() || null }),
        },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['pipeline-insights', 'deal', 'candidate', vars.dealKey] });
      qc.invalidateQueries({ queryKey: ['pipeline-insights', 'aggregator-run'] });
      qc.invalidateQueries({ queryKey: ['trace', 'backward'] });
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}


// ===========================================================================
// Health mode (PR 2)
// ===========================================================================

export type IssueSeverity = 'red' | 'amber' | 'ok';

export interface IssueAction {
  action: 'view_cohort' | 'open_prompts' | 'filter_ledger';
  label: string;
  cohort?: string;
  purpose?: string;
  params?: Record<string, unknown>;
}

export interface HealthIssue {
  severity: IssueSeverity;
  kind: string;
  title: string;
  detail: string;
  value: number | null;
  actions: IssueAction[];
}

export interface HealthIssuesResponse {
  generated_at: string;
  thresholds: Record<string, number>;
  issues: HealthIssue[];
}

export function useHealthIssues() {
  return useQuery<HealthIssuesResponse>({
    queryKey: ['pipeline-insights', 'health-issues'],
    queryFn: () =>
      apiFetch<HealthIssuesResponse>(
        '/api/admin/pipeline-insights/health-issues',
      ),
    staleTime: 60_000,
  });
}

// ===========================================================================
// Review-queue cohorts (PR 3)
// ===========================================================================

export interface CohortItem {
  kind: 'signal' | 'field_edit' | 'deal';
  // signal
  signal_id?: number;
  sender?: string;
  subject?: string;
  received_at?: string | null;
  classifier_reason?: string;
  signal_type?: string;
  extracted_name?: string | null;
  // field_edit
  deal_kind?: 'candidate' | 'affinity_opp';
  deal_key?: string;
  field_name?: string;
  proposed_value?: unknown;
  user_value?: unknown;
  proposed_decision?: string;
  proposed_confidence?: string;
  edited_by?: string;
  edited_at?: string | null;
  // deal cohort
  refute_count?: number;
  last_refute_at?: string | null;

  view_in_pipeline_insights?: {
    kind: 'signal' | 'deal';
    signal_id?: number;
    deal_kind?: 'candidate' | 'affinity_opp';
    deal_key?: string;
  };
}

export interface CohortResponse {
  cohort_kind: string;
  window_days: number;
  items: CohortItem[];
}

export function useCohort(
  cohort: string | null,
  params: Record<string, string | number | undefined> = {},
) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined) qs.set(k, String(v));
  });
  const qsStr = qs.toString();
  return useQuery<CohortResponse>({
    queryKey: ['pipeline-insights', 'cohort', cohort, params],
    queryFn: () =>
      apiFetch<CohortResponse>(
        `/api/admin/pipeline-insights/cohort/${cohort}${qsStr ? '?' + qsStr : ''}`,
      ),
    enabled: cohort !== null,
    staleTime: 60_000,
  });
}


// ── Cross-pipeline events (multi-pipeline observability slice 2) ────────────

export interface PipelineEventArtifact {
  kind: string;
  id: number;
  status: string | null;
  deep_link: string;
  /** Human display name for an nda_review artifact (bound label → codename → "Review N"). */
  label?: string | null;
}

export interface PipelineEventContext {
  subject?: string;
  sender?: string;
  /** classifier signal_type, surfaced as the verdict. */
  verdict?: string;
  confidence?: string;
}

export interface PipelineEventRow {
  event_log_id: number;
  pipeline: string;
  event_type: string;
  occurred_at: string | null;
  event_source_id: string | null;
  deal_kind: string | null;
  deal_key: string | null;
  /** Resolved deal name for deal-pipeline rows (null when unresolvable). */
  deal_label: string | null;
  /** Deal-card deep link (`/deals?deal_key=…`); null when no name resolved. */
  deal_link: string | null;
  /** Email/signal context from the bound origination_signal (null when none). */
  context: PipelineEventContext | null;
  artifact: PipelineEventArtifact | null;
  unresolved_state: string | null;
}

export interface PipelineEventFacet {
  pipeline: string;
  label: string;
  count: number;
}

export interface PipelineEventsResponse {
  events: PipelineEventRow[];
  facets: PipelineEventFacet[];
  has_more: boolean;
  offset: number;
  limit: number;
}

export function usePipelineEvents(params: {
  pipeline?: string | null;
  unresolved?: boolean;
  sinceDays?: number;
}) {
  const qs = new URLSearchParams();
  if (params.pipeline) qs.set('pipeline', params.pipeline);
  if (params.unresolved) qs.set('unresolved', 'true');
  if (params.sinceDays) qs.set('since_days', String(params.sinceDays));
  const qsStr = qs.toString();
  return useQuery<PipelineEventsResponse>({
    queryKey: ['pipeline-insights', 'events', params],
    queryFn: () =>
      apiFetch<PipelineEventsResponse>(
        `/api/admin/pipeline-insights/events${qsStr ? '?' + qsStr : ''}`,
      ),
    staleTime: 30_000,
  });
}
